// ************************************************************************************************
// pathname: /v2/publication/calculate-price/{jobSlug}
// ************************************************************************************************
import { useSwrApi } from 'src/hooks/useSwrApi';

/** Props */
interface UseSwrCalculatePriceProps {
  locale: Locale,
  jobSlug?: string,
  payload?: Omit<
  Partial<ApiSchema<'GeneralActivationData'>>,
  'jobModel'
  > & {
    jobModel?: Partial<ApiSchema<'JobModel3'>> & {
      withoutCharge?: boolean;
    };
  };
  shouldFetch?: boolean,
  successCallback?: (data?: ApiSchema<'UpgradePrice'>) => void,
  onDataChanged?: (data?: ApiSchema<'UpgradePrice'>) => void,
}

/**
 * @description SWR: fetch calculate-price data.
 * - `locale` - the current locale
 * - `jobSlug` - the job slug
 * - `postData` - the post data object
 * - `shouldFetch` - whether to fetch the data; default is true
 * - `successCallback` - success callback function; works only on successful fetch
 * - `onDataChanged` - data changed callback function; works on every data change (from cache or fetch)
 */
export const useSwrCalculatePrice = (props: UseSwrCalculatePriceProps) => {
  const {
    locale,
    jobSlug,
    payload = {},
    shouldFetch = true,
    successCallback,
    onDataChanged,
  } = props;

  return useSwrApi<ApiSchema<'UpgradePrice'>>({
    url: `/v2/publication/calculate-price${jobSlug ? `/${jobSlug}` : ''}`,
    locale,
    shouldFetch,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      payload,
    },
    apiOptions: {
      revalidateOnFocus: false,
      keepPreviousData: true,
      dedupingInterval: 0, // disabled
      onSuccess: (response) => {
        if (successCallback) successCallback(response);
      },
    },
    onDataChange: (data) => {
      if (onDataChanged) onDataChanged(data);
    },
  });
};
